import { jsPDF } from 'jspdf';
import { Document, Packer, Paragraph, TextRun, AlignmentType, Header, ImageRun, Table, TableRow, TableCell, WidthType, Footer, PageNumber, convertInchesToTwip, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';
import type { Chat, Message, ChatMode } from './chatService';

export interface BrandingData {
  brand: string;
  subtitle: string;
  footer: string;
}

/**
 * Get branding data based on chat mode or context
 */
export function getBrandingData(mode?: ChatMode): BrandingData {
  const m = mode || 'funcionarios';
  
  if (m === 'funcionarios') {
    return {
      brand: 'MUNILEX',
      subtitle: 'Asistente Jurídico para Entidades Locales',
      footer: 'Documento generado por Munilex – Asistente Jurídico para Entidades Locales'
    };
  }

  const mapping: Record<string, string> = {
    'oposiciones': 'Administración Local',
    'oposiciones_c1': 'C1 Administrativo',
    'oposiciones_c2': 'C2 Auxiliar',
    'oposiciones_guardia_civil': 'Guardia Civil',
    'oposiciones_iipp': 'Instituciones Penitenciarias'
  };

  const opoName = mapping[m] || 'Oposiciones';
  return {
    brand: 'MUNILEX ACADEMY',
    subtitle: `Tu asistente para la opo ${opoName}`,
    footer: `Documento generado por Munilex Academy – Tu asistente para la opo ${opoName}`
  };
}

// Import the logo as base64 - we'll fetch it dynamically
const MUNILEX_LOGO_PATH = '/munilex-logo.png';

/**
 * Convert base64 string to Uint8Array (browser-compatible alternative to Buffer)
 */
function base64ToUint8Array(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

// Document detection patterns - flexible to catch various formats
const DOCUMENT_PATTERNS = [
  // Actas
  { regex: /(?:^|\n)(?:#{1,3}\s*)?ACTA\s+DE\s+/im, type: 'acta', prefix: 'Acta' },
  { regex: /(?:^|\n)\*{0,2}ACTA\s+DE\s+/im, type: 'acta', prefix: 'Acta' },
  // Informes
  { regex: /(?:^|\n)(?:#{1,3}\s*)?INFORME\s+/im, type: 'informe', prefix: 'Informe' },
  { regex: /(?:^|\n)\*{0,2}INFORME\s+(?:DE\s+|SOBRE\s+|TÉCNICO|JURÍDICO)/im, type: 'informe', prefix: 'Informe' },
  // Dictámenes
  { regex: /(?:^|\n)(?:#{1,3}\s*)?DICTAMEN\s+/im, type: 'dictamen', prefix: 'Dictamen' },
  { regex: /(?:^|\n)\*{0,2}DICTAMEN\s+/im, type: 'dictamen', prefix: 'Dictamen' },
  // Decretos
  { regex: /(?:^|\n)(?:#{1,3}\s*)?DECRETO\s+/im, type: 'decreto', prefix: 'Decreto' },
  { regex: /(?:^|\n)\*{0,2}DECRETO\s+(?:DE\s+|NÚM|N\.?º)/im, type: 'decreto', prefix: 'Decreto' },
  // Resoluciones
  { regex: /(?:^|\n)(?:#{1,3}\s*)?RESOLUCIÓN\s+/im, type: 'resolucion', prefix: 'Resolución' },
  { regex: /(?:^|\n)\*{0,2}RESOLUCIÓN\s+/im, type: 'resolucion', prefix: 'Resolución' },
  // Certificados
  { regex: /(?:^|\n)(?:#{1,3}\s*)?CERTIFICADO\s+/im, type: 'certificado', prefix: 'Certificado' },
  { regex: /(?:^|\n)\*{0,2}CERTIFICADO\s+/im, type: 'certificado', prefix: 'Certificado' },
  // Providencias
  { regex: /(?:^|\n)(?:#{1,3}\s*)?PROVIDENCIA\s+/im, type: 'providencia', prefix: 'Providencia' },
  // Diligencias
  { regex: /(?:^|\n)(?:#{1,3}\s*)?DILIGENCIA\s+/im, type: 'diligencia', prefix: 'Diligencia' },
  // Modelos
  { regex: /(?:^|\n)(?:#{1,3}\s*)?MODELO\s+(?:DE\s+)?/im, type: 'modelo', prefix: 'Modelo' },
  { regex: /(?:^|\n)\*{0,2}MODELO\s+(?:DE\s+)?/im, type: 'modelo', prefix: 'Modelo' },
  // Propuestas
  { regex: /(?:^|\n)(?:#{1,3}\s*)?PROPUESTA\s+/im, type: 'propuesta', prefix: 'Propuesta' },
  // Convenios
  { regex: /(?:^|\n)(?:#{1,3}\s*)?CONVENIO\s+/im, type: 'convenio', prefix: 'Convenio' },
  // Contratos
  { regex: /(?:^|\n)(?:#{1,3}\s*)?CONTRATO\s+/im, type: 'contrato', prefix: 'Contrato' },
  // Ordenanzas
  { regex: /(?:^|\n)(?:#{1,3}\s*)?ORDENANZA\s+/im, type: 'ordenanza', prefix: 'Ordenanza' },
  // Reglamentos
  { regex: /(?:^|\n)(?:#{1,3}\s*)?REGLAMENTO\s+/im, type: 'reglamento', prefix: 'Reglamento' },
  // Pliego de condiciones
  { regex: /(?:^|\n)(?:#{1,3}\s*)?PLIEGO\s+(?:DE\s+)?/im, type: 'pliego', prefix: 'Pliego' },
  // Notificaciones
  { regex: /(?:^|\n)(?:#{1,3}\s*)?NOTIFICACIÓN\s+/im, type: 'notificacion', prefix: 'Notificación' },
  // Solicitudes
  { regex: /(?:^|\n)(?:#{1,3}\s*)?SOLICITUD\s+/im, type: 'solicitud', prefix: 'Solicitud' },
  // Escritos genéricos
  { regex: /(?:^|\n)(?:#{1,3}\s*)?ESCRITO\s+/im, type: 'escrito', prefix: 'Escrito' },
];

export interface DocumentInfo {
  isDocument: boolean;
  type: string;
  title: string;
  filename: string;
}

/**
 * Detect if content is a formal document
 */
export function detectDocument(content: string): DocumentInfo {
  for (const pattern of DOCUMENT_PATTERNS) {
    if (pattern.regex.test(content)) {
      let title = pattern.prefix;
      
      const mdTitleMatch = content.match(/^#{1,3}\s*(.+?)$/m);
      if (mdTitleMatch) {
        title = mdTitleMatch[1].trim();
      } else {
        const boldTitleMatch = content.match(/^\*\*(.+?)\*\*/m);
        if (boldTitleMatch) {
          title = boldTitleMatch[1].trim();
        } else {
          const uppercaseMatch = content.match(/^([A-ZÁÉÍÓÚÑ][A-ZÁÉÍÓÚÑ\s]+(?:DE\s+[A-ZÁÉÍÓÚÑ\s]+)?)/m);
          if (uppercaseMatch) {
            title = uppercaseMatch[1].trim();
          }
        }
      }
      
      const sanitizedTitle = title
        .replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s]/g, '')
        .trim()
        .substring(0, 50);
      const filename = `${sanitizedTitle || pattern.prefix}_${new Date().toISOString().split('T')[0]}`;
      
      return {
        isDocument: true,
        type: pattern.type,
        title,
        filename
      };
    }
  }
  
  return {
    isDocument: false,
    type: '',
    title: '',
    filename: ''
  };
}

/**
 * Export chat conversation to PDF - Formato institucional
 */
export async function exportChatToPDF(chat: Chat, messages: Message[]): Promise<void> {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  
  // Márgenes institucionales: Superior 25mm, Inferior 25mm, Izquierdo 30mm, Derecho 25mm
  const marginTop = 25;
  const marginBottom = 25;
  const marginLeft = 30;
  const marginRight = 25;
  const contentWidth = pageWidth - marginLeft - marginRight;
  let yPosition = marginTop;

  // Color negro para texto institucional
  const blackColor: [number, number, number] = [0, 0, 0];
  const grayColor: [number, number, number] = [128, 128, 128];

  const branding = getBrandingData(chat.chat_mode);

  // Función para añadir cabecera
  const addHeader = () => {
    // Logo Munilex (texto si no hay imagen)
    pdf.setFont('times', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text(branding.brand, marginLeft, 12);
    
    pdf.setFont('times', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(128, 128, 128);
    pdf.text(branding.subtitle, marginLeft, 16);
    
    // Línea separadora sutil
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.3);
    pdf.line(marginLeft, 20, pageWidth - marginRight, 20);
  };

  // Función para añadir pie de página
  const addFooter = (pageNum: number, totalPages: number) => {
    pdf.setFont('times', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(128, 128, 128);
    pdf.text(branding.footer, marginLeft, pageHeight - 12);
    pdf.text(`Página ${pageNum} de ${totalPages}`, pageWidth - marginRight - 20, pageHeight - 10);
  };

  // Primera página
  addHeader();
  yPosition = marginTop + 5;

  // Título del documento
  pdf.setTextColor(...blackColor);
  pdf.setFontSize(14);
  pdf.setFont('times', 'bold');
  const titleLines = pdf.splitTextToSize(chat.title || 'Conversación', contentWidth);
  pdf.text(titleLines, marginLeft, yPosition);
  yPosition += titleLines.length * 7 + 5;

  // Fecha
  pdf.setTextColor(...grayColor);
  pdf.setFontSize(10);
  pdf.setFont('times', 'normal');
  const dateStr = new Date(chat.created_at).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
  pdf.text(`Fecha: ${dateStr}`, marginLeft, yPosition);
  yPosition += 12;

  // Mensajes
  pdf.setFont('times', 'normal');
  pdf.setFontSize(12);
  
  for (const message of messages) {
    if (yPosition > pageHeight - marginBottom - 20) {
      pdf.addPage();
      addHeader();
      yPosition = marginTop + 5;
    }

    const isUser = message.role === 'user';
    const roleLabel = isUser ? 'Consulta:' : 'Respuesta:';

    // Etiqueta del rol
    pdf.setTextColor(...blackColor);
    pdf.setFontSize(11);
    pdf.setFont('times', 'bold');
    pdf.text(roleLabel, marginLeft, yPosition);
    yPosition += 6;

    // Contenido del mensaje - limpiar markdown
    const cleanContent = message.content
      .replace(/#{1,6}\s*/g, '')
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/`(.*?)`/g, '$1')
      .replace(/^\s*[-*]\s+/gm, '• ')
      .replace(/---+/g, ''); // Eliminar líneas horizontales

    pdf.setTextColor(...blackColor);
    pdf.setFontSize(12);
    pdf.setFont('times', 'normal');
    
    const contentLines = pdf.splitTextToSize(cleanContent, contentWidth);
    
    for (const line of contentLines) {
      if (yPosition > pageHeight - marginBottom - 10) {
        pdf.addPage();
        addHeader();
        yPosition = marginTop + 5;
      }
      pdf.text(line, marginLeft, yPosition);
      yPosition += 6; // Interlineado 1.5 aprox
    }

    yPosition += 10;
  }

  // Añadir pies de página a todas las páginas
  const totalPages = pdf.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    addFooter(i, totalPages);
  }

  // Guardar archivo
  const filename = `${chat.title?.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ]/g, '_') || 'conversacion'}_${new Date().toISOString().split('T')[0]}.pdf`;
  pdf.save(filename);
}

/**
 * Fetch logo as base64
 */
async function fetchLogoAsBase64(): Promise<string | null> {
  try {
    const response = await fetch('/munilex-logo.png');
    if (!response.ok) {
      console.warn('Could not fetch logo');
      return null;
    }
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        const base64Data = base64.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Error fetching logo:', error);
    return null;
  }
}

/**
 * Parse markdown table into rows and cells
 */
function parseMarkdownTable(tableLines: string[]): { headers: string[]; rows: string[][] } | null {
  if (tableLines.length < 2) return null;
  
  const parseRow = (line: string): string[] => {
    return line
      .split('|')
      .map(cell => cell.trim())
      .filter((_, index, arr) => index > 0 && index < arr.length - 1 || (arr.length === 1 && index === 0));
  };
  
  const headers = parseRow(tableLines[0]);
  if (headers.length === 0) return null;
  
  const separatorLine = tableLines[1];
  if (!separatorLine.includes('-')) return null;
  
  const rows: string[][] = [];
  for (let i = 2; i < tableLines.length; i++) {
    const row = parseRow(tableLines[i]);
    if (row.length > 0) {
      rows.push(row);
    }
  }
  
  return { headers, rows };
}

/**
 * Create a Word table - formato institucional sin colores
 */
function createWordTable(tableData: { headers: string[]; rows: string[][] }): Table {
  // Header row
  const headerRow = new TableRow({
    children: tableData.headers.map(header => 
      new TableCell({
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: header,
                bold: true,
                color: "000000",
                font: "Times New Roman",
                size: 24 // 12pt
              })
            ],
            alignment: AlignmentType.CENTER
          })
        ],
        shading: {
          fill: "F0F0F0"
        },
        margins: {
          top: convertInchesToTwip(0.05),
          bottom: convertInchesToTwip(0.05),
          left: convertInchesToTwip(0.1),
          right: convertInchesToTwip(0.1)
        }
      })
    ),
    tableHeader: true
  });
  
  // Data rows
  const dataRows = tableData.rows.map(row => 
    new TableRow({
      children: row.map(cell => 
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: cell,
                  font: "Times New Roman",
                  size: 24,
                  color: "000000"
                })
              ],
              alignment: AlignmentType.LEFT
            })
          ],
          margins: {
            top: convertInchesToTwip(0.04),
            bottom: convertInchesToTwip(0.04),
            left: convertInchesToTwip(0.1),
            right: convertInchesToTwip(0.1)
          }
        })
      )
    })
  );
  
  return new Table({
    rows: [headerRow, ...dataRows],
    width: {
      size: 100,
      type: WidthType.PERCENTAGE
    },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
      bottom: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
      left: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
      right: { style: BorderStyle.SINGLE, size: 4, color: "000000" },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: "666666" },
      insideVertical: { style: BorderStyle.SINGLE, size: 2, color: "666666" }
    }
  });
}

/**
 * Export document content to Word - Formato institucional administrativo
 * Válido para expediente administrativo y firma electrónica
 */
export async function exportToWord(content: string, filename: string, chatMode?: ChatMode): Promise<void> {
  const branding = getBrandingData(chatMode);
  const logoBase64 = await fetchLogoAsBase64();
  
  const lines = content.split('\n');
  const children: (Paragraph | Table)[] = [];
  
  // Fecha actual formateada
  const today = new Date().toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  // Espaciado inicial después del encabezado
  children.push(new Paragraph({ text: '', spacing: { after: 200 } }));

  let i = 0;
  while (i < lines.length) {
    const trimmedLine = lines[i].trim();
    
    // Ignorar líneas horizontales de markdown (---, ___, ***)
    if (/^[-_*]{3,}$/.test(trimmedLine)) {
      i++;
      continue;
    }
    
    if (!trimmedLine) {
      children.push(new Paragraph({ text: '', spacing: { after: 120 } })); // 6pt después
      i++;
      continue;
    }

    // Tablas
    if (trimmedLine.startsWith('|')) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i].trim());
        i++;
      }
      
      const tableData = parseMarkdownTable(tableLines);
      if (tableData) {
        children.push(new Paragraph({ text: '', spacing: { after: 120 } }));
        children.push(createWordTable(tableData));
        children.push(new Paragraph({ text: '', spacing: { after: 200 } }));
      }
      continue;
    }

    // Título principal (H1) - 14pt, negrita, centrado
    if (trimmedLine.startsWith('# ')) {
      const titleText = trimmedLine.substring(2).replace(/\*\*/g, '');
      const isNumberedSection = /^\d+\.\s/.test(titleText);
      
      if (isNumberedSection) {
        // Sección numerada - 12pt negrita, alineado izquierda
        children.push(new Paragraph({
          children: [
            new TextRun({
              text: titleText.toUpperCase(),
              bold: true,
              size: 24, // 12pt
              color: "000000",
              font: "Times New Roman"
            })
          ],
          alignment: AlignmentType.LEFT,
          spacing: { before: 240, after: 120, line: 276 } // 1.15 interlineado para títulos
        }));
      } else {
        // Título principal - 14pt negrita, centrado
        children.push(new Paragraph({
          children: [
            new TextRun({
              text: titleText.toUpperCase(),
              bold: true,
              size: 28, // 14pt
              color: "000000",
              font: "Times New Roman"
            })
          ],
          alignment: AlignmentType.CENTER,
          spacing: { before: 240, after: 240, line: 276 }
        }));
      }
      i++;
      continue;
    }

    // Subtítulo (H2) - 12pt negrita
    if (trimmedLine.startsWith('## ')) {
      const subtitleText = trimmedLine.substring(3).replace(/\*\*/g, '');
      children.push(new Paragraph({
        children: [
          new TextRun({
            text: subtitleText.toUpperCase(),
            bold: true,
            size: 24, // 12pt
            color: "000000",
            font: "Times New Roman"
          })
        ],
        alignment: AlignmentType.LEFT,
        spacing: { before: 240, after: 120, line: 276 }
      }));
      i++;
      continue;
    }

    // Encabezado de sección (H3) - 12pt negrita
    if (trimmedLine.startsWith('### ')) {
      const sectionText = trimmedLine.substring(4).replace(/\*\*/g, '');
      children.push(new Paragraph({
        children: [
          new TextRun({
            text: sectionText,
            bold: true,
            size: 24, // 12pt
            color: "000000",
            font: "Times New Roman"
          })
        ],
        spacing: { before: 200, after: 120, line: 276 }
      }));
      i++;
      continue;
    }

    // Fórmulas institucionales en mayúsculas (CERTIFICA, ACUERDA, RESULTANDO, etc.)
    const formulaMatch = trimmedLine.match(/^(CERTIFIC[AO]|ACUERD[AO]|RESULTANDO|CONSIDERANDO|VISTO|PRIMERO|SEGUNDO|TERCERO|CUARTO|QUINTO|SEXTO|SÉPTIMO|OCTAVO|NOVENO|DÉCIMO|ANTECEDENTES?|FUNDAMENTOS?|PROPUESTA|RESOLUCIÓN|DISPONGO|ORDENO)[\s:.-]*/i);
    if (formulaMatch) {
      const formula = formulaMatch[1].toUpperCase();
      const restOfLine = trimmedLine.substring(formulaMatch[0].length);
      
      const textRuns: TextRun[] = [
        new TextRun({
          text: formula + (restOfLine ? ': ' : ''),
          bold: true,
          size: 24,
          color: "000000",
          font: "Times New Roman"
        })
      ];
      
      if (restOfLine) {
        textRuns.push(...parseTextRunsWithFormatting(restOfLine));
      }
      
      children.push(new Paragraph({
        children: textRuns,
        spacing: { before: 200, after: 120, line: 360 }, // 1.5 interlineado
        alignment: AlignmentType.JUSTIFIED
      }));
      i++;
      continue;
    }

    // Lista con viñetas
    if (trimmedLine.match(/^[-*]\s+/)) {
      const text = trimmedLine.replace(/^[-*]\s+/, '');
      const textRuns = parseTextRunsWithFormatting(text);
      children.push(new Paragraph({
        children: [
          new TextRun({ text: "• ", font: "Times New Roman", size: 24 }),
          ...textRuns
        ],
        indent: { left: convertInchesToTwip(0.4) },
        spacing: { before: 60, after: 60, line: 360 },
        alignment: AlignmentType.JUSTIFIED
      }));
      i++;
      continue;
    }

    // Lista numerada
    if (trimmedLine.match(/^\d+\.\s+/)) {
      const match = trimmedLine.match(/^(\d+)\.\s+(.+)/);
      if (match) {
        const textRuns = parseTextRunsWithFormatting(match[2]);
        children.push(new Paragraph({
          children: [
            new TextRun({ text: `${match[1]}. `, font: "Times New Roman", size: 24, bold: true }),
            ...textRuns
          ],
          indent: { left: convertInchesToTwip(0.4) },
          spacing: { before: 60, after: 60, line: 360 },
          alignment: AlignmentType.JUSTIFIED
        }));
      }
      i++;
      continue;
    }

    // Párrafo regular - justificado, interlineado 1.5
    const textRuns = parseTextRunsWithFormatting(trimmedLine);
    children.push(new Paragraph({
      children: textRuns,
      spacing: { before: 120, after: 120, line: 360 }, // 1.5 interlineado (360 twips = 1.5 lines)
      alignment: AlignmentType.JUSTIFIED
    }));
    i++;
  }

  // Añadir cierre institucional si no existe
  const hasClosing = content.toLowerCase().includes('documento firmado') || 
                     content.toLowerCase().includes('firma electrónica');
  
  if (!hasClosing) {
    children.push(new Paragraph({ text: '', spacing: { after: 400 } }));
    children.push(new Paragraph({
      children: [
        new TextRun({
          text: 'Documento firmado electrónicamente',
          italics: true,
          size: 22, // 11pt
          color: "666666",
          font: "Times New Roman"
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { before: 200, after: 100 }
    }));
  }

  // Crear cabecera con logo
  const headerChildren: Paragraph[] = [];
  
  if (logoBase64) {
    headerChildren.push(
      new Paragraph({
        children: [
          new ImageRun({
            data: base64ToUint8Array(logoBase64),
            transformation: {
              width: 85, // ~3cm aproximadamente
              height: 28
            },
            type: 'png'
          })
        ],
        alignment: AlignmentType.LEFT
      })
    );
    headerChildren.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `${branding.brand} – ${branding.subtitle}`,
            size: 18, // 9pt
            color: "666666",
            font: "Times New Roman"
          })
        ],
        alignment: AlignmentType.LEFT,
        spacing: { after: 100 }
      })
    );
  } else {
    headerChildren.push(
      new Paragraph({
        children: [
          new TextRun({
            text: branding.brand,
            bold: true,
            size: 22,
            color: "000000",
            font: "Times New Roman"
          })
        ],
        alignment: AlignmentType.LEFT
      })
    );
    headerChildren.push(
      new Paragraph({
        children: [
          new TextRun({
            text: branding.subtitle,
            size: 18,
            color: "666666",
            font: "Times New Roman"
          })
        ],
        alignment: AlignmentType.LEFT,
        spacing: { after: 100 }
      })
    );
  }

  // Crear pie de página - discreto, gris
  const footerParagraph = new Paragraph({
    children: [
      new TextRun({
        text: branding.footer,
        size: 18, // 9pt
        color: "999999",
        font: "Times New Roman"
      }),
      new TextRun({
        text: "     |     Página ",
        size: 18,
        color: "999999",
        font: "Times New Roman"
      }),
      new TextRun({
        children: [PageNumber.CURRENT],
        size: 18,
        color: "999999",
        font: "Times New Roman"
      }),
      new TextRun({
        text: " de ",
        size: 18,
        color: "999999",
        font: "Times New Roman"
      }),
      new TextRun({
        children: [PageNumber.TOTAL_PAGES],
        size: 18,
        color: "999999",
        font: "Times New Roman"
      })
    ],
    alignment: AlignmentType.CENTER
  });

  // Crear documento con márgenes institucionales
  // Superior: 2.5cm, Inferior: 2.5cm, Izquierdo: 3cm, Derecho: 2.5cm
  const doc = new Document({
    styles: {
      default: {
        document: {
          run: {
            font: "Times New Roman",
            size: 24 // 12pt por defecto
          }
        }
      }
    },
    sections: [{
      properties: {
        page: {
          margin: {
            top: convertInchesToTwip(0.98), // 2.5cm
            right: convertInchesToTwip(0.98), // 2.5cm
            bottom: convertInchesToTwip(0.98), // 2.5cm
            left: convertInchesToTwip(1.18) // 3cm
          }
        }
      },
      headers: {
        default: new Header({
          children: headerChildren
        })
      },
      footers: {
        default: new Footer({
          children: [footerParagraph]
        })
      },
      children
    }]
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${filename}.docx`);
}

/**
 * Parse text with markdown formatting to TextRuns - Times New Roman, negro
 */
function parseTextRunsWithFormatting(text: string): TextRun[] {
  const runs: TextRun[] = [];
  
  // Eliminar líneas horizontales
  text = text.replace(/---+/g, '');
  
  const boldParts = text.split(/(\*\*.*?\*\*)/g);
  
  for (const part of boldParts) {
    if (!part) continue;
    
    if (part.startsWith('**') && part.endsWith('**')) {
      const boldText = part.slice(2, -2);
      const italicParts = boldText.split(/(\*.*?\*)/g);
      for (const italicPart of italicParts) {
        if (!italicPart) continue;
        if (italicPart.startsWith('*') && italicPart.endsWith('*')) {
          runs.push(new TextRun({
            text: italicPart.slice(1, -1),
            bold: true,
            italics: true,
            font: "Times New Roman",
            size: 24,
            color: "000000"
          }));
        } else {
          runs.push(new TextRun({
            text: italicPart,
            bold: true,
            font: "Times New Roman",
            size: 24,
            color: "000000"
          }));
        }
      }
    } else {
      const italicParts = part.split(/(\*.*?\*)/g);
      for (const italicPart of italicParts) {
        if (!italicPart) continue;
        if (italicPart.startsWith('*') && italicPart.endsWith('*')) {
          runs.push(new TextRun({
            text: italicPart.slice(1, -1),
            italics: true,
            font: "Times New Roman",
            size: 24,
            color: "000000"
          }));
        } else {
          // Manejar código inline
          const codeParts = italicPart.split(/(`.*?`)/g);
          for (const codePart of codeParts) {
            if (!codePart) continue;
            if (codePart.startsWith('`') && codePart.endsWith('`')) {
              runs.push(new TextRun({
                text: codePart.slice(1, -1),
                font: "Courier New",
                size: 22,
                color: "000000"
              }));
            } else {
              runs.push(new TextRun({
                text: codePart,
                font: "Times New Roman",
                size: 24,
                color: "000000"
              }));
            }
          }
        }
      }
    }
  }
  
  return runs.length > 0 ? runs : [new TextRun({ text, font: "Times New Roman", size: 24, color: "000000" })];
}

/**
 * Export markdown content to PDF - Formato institucional con jerarquía, tablas y negritas
 */
/**
 * Create generic content PDF document
 */
export async function createPdfDocument(content: string, filename: string, chatMode?: ChatMode): Promise<jsPDF> {
  const branding = getBrandingData(chatMode);
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  
  // Márgenes institucionales
  const marginTop = 25;
  const marginBottom = 25;
  const marginLeft = 30;
  const marginRight = 25;
  const contentWidth = pageWidth - marginLeft - marginRight;
  let yPosition = marginTop;

  // Función para añadir cabecera
  const addHeader = () => {
    pdf.setFont('times', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text(branding.brand, marginLeft, 12);
    
    pdf.setFont('times', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(128, 128, 128);
    pdf.text(branding.subtitle, marginLeft, 16);
    
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.3);
    pdf.line(marginLeft, 20, pageWidth - marginRight, 20);
  };

  // Función para añadir pie de página
  const addFooter = (pageNum: number, totalPages: number) => {
    pdf.setFont('times', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(128, 128, 128);
    pdf.text(branding.footer, marginLeft, pageHeight - 12);
    pdf.text(`Página ${pageNum} de ${totalPages}`, pageWidth - marginRight - 20, pageHeight - 10);
  };

  // Salto de página si no queda espacio
  const ensureSpace = (needed: number) => {
    if (yPosition + needed > pageHeight - marginBottom - 10) {
      pdf.addPage();
      addHeader();
      yPosition = marginTop + 5;
    }
  };

  /**
   * Render a line of text with inline **bold** support
   */
  const renderFormattedLine = (text: string, x: number, y: number, maxWidth: number, baseFontSize: number, baseStyle: string) => {
    // Split by bold markers
    const segments = text.split(/(\*\*.*?\*\*)/g);
    let currentX = x;

    for (const seg of segments) {
      if (!seg) continue;
      const isBold = seg.startsWith('**') && seg.endsWith('**');
      const cleanText = isBold ? seg.slice(2, -2) : seg;
      if (!cleanText) continue;

      pdf.setFont('times', isBold ? 'bold' : baseStyle);
      pdf.setFontSize(baseFontSize);

      const segWidth = pdf.getTextWidth(cleanText);
      
      // If the segment would overflow, just let jsPDF handle it simply
      if (currentX + segWidth > x + maxWidth) {
        // Wrap: print what fits on this line, then continue on the next
        const words = cleanText.split(' ');
        for (const word of words) {
          const wordWidth = pdf.getTextWidth(word + ' ');
          if (currentX + wordWidth > x + maxWidth && currentX > x) {
            // New line
            y += baseFontSize * 0.5;
            yPosition = y;
            ensureSpace(baseFontSize * 0.5);
            y = yPosition;
            currentX = x;
          }
          pdf.text(word + ' ', currentX, y);
          currentX += wordWidth;
        }
      } else {
        pdf.text(cleanText, currentX, y);
        currentX += segWidth;
      }
    }
    return y;
  };

  /**
   * Print a paragraph with word-wrap and inline bold, returning final yPosition
   */
  const printFormattedParagraph = (text: string, fontSize: number, fontStyle: string, indent: number = 0, spacingAfter: number = 5) => {
    const cleanText = text.replace(/`(.*?)`/g, '$1');
    
    // For lines with bold segments, we need to handle wrapping ourselves
    const hasBold = cleanText.includes('**');
    
    if (!hasBold) {
      // Simple case: no inline formatting, use jsPDF built-in wrapping
      pdf.setFont('times', fontStyle);
      pdf.setFontSize(fontSize);
      pdf.setTextColor(0, 0, 0);
      const wrappedLines = pdf.splitTextToSize(cleanText, contentWidth - indent);
      for (const wLine of wrappedLines) {
        ensureSpace(fontSize * 0.5);
        pdf.text(wLine, marginLeft + indent, yPosition);
        yPosition += fontSize * 0.5;
      }
    } else {
      // Complex case: split into sentences/chunks and render with bold support
      // First, split by sentences or natural breaks to keep lines manageable
      pdf.setFont('times', fontStyle);
      pdf.setFontSize(fontSize);
      
      // Word-wrap manually considering bold
      const words = cleanText.split(' ');
      let currentLine = '';
      const lines: string[] = [];

      for (const word of words) {
        const testLine = currentLine ? currentLine + ' ' + word : word;
        // Approximate width: strip bold markers for measurement
        const plainTest = testLine.replace(/\*\*/g, '');
        pdf.setFont('times', 'normal');
        const testWidth = pdf.getTextWidth(plainTest);
        if (testWidth > contentWidth - indent && currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }
      if (currentLine) lines.push(currentLine);

      for (const line of lines) {
        ensureSpace(fontSize * 0.5);
        // Render each line segment by segment
        const segments = line.split(/(\*\*.*?\*\*)/g);
        let currentX = marginLeft + indent;
        for (const seg of segments) {
          if (!seg) continue;
          const isBold = seg.startsWith('**') && seg.endsWith('**');
          const segText = isBold ? seg.slice(2, -2) : seg;
          if (!segText) continue;
          pdf.setFont('times', isBold ? 'bold' : fontStyle);
          pdf.setFontSize(fontSize);
          pdf.text(segText, currentX, yPosition);
          currentX += pdf.getTextWidth(segText);
        }
        yPosition += fontSize * 0.5;
      }
    }
    yPosition += spacingAfter;
  };

  /**
   * Render a markdown table in the PDF
   */
  const renderPdfTable = (tableLines: string[]) => {
    const tableData = parseMarkdownTable(tableLines);
    if (!tableData) return;

    const { headers, rows } = tableData;
    const numCols = headers.length;
    const colWidth = contentWidth / numCols;
    const cellPadding = 2;
    const cellFontSize = 9;
    const headerFontSize = 9;

    // Calculate row heights
    const calcRowHeight = (cells: string[], fontSize: number): number => {
      let maxH = fontSize * 0.45 + cellPadding * 2;
      pdf.setFontSize(fontSize);
      for (let c = 0; c < cells.length; c++) {
        const cellText = (cells[c] || '').replace(/\*\*/g, '');
        const lines = pdf.splitTextToSize(cellText, colWidth - cellPadding * 2);
        const h = lines.length * fontSize * 0.45 + cellPadding * 2;
        if (h > maxH) maxH = h;
      }
      return maxH;
    };

    // Header
    const headerH = calcRowHeight(headers, headerFontSize);
    ensureSpace(headerH + 4);
    yPosition += 2;

    // Draw header background
    pdf.setFillColor(240, 240, 240);
    pdf.rect(marginLeft, yPosition - 1, contentWidth, headerH, 'F');
    
    // Draw header cells
    pdf.setFont('times', 'bold');
    pdf.setFontSize(headerFontSize);
    pdf.setTextColor(0, 0, 0);
    for (let c = 0; c < numCols; c++) {
      const cellX = marginLeft + c * colWidth + cellPadding;
      const cellText = (headers[c] || '').replace(/\*\*/g, '');
      const lines = pdf.splitTextToSize(cellText, colWidth - cellPadding * 2);
      let lineY = yPosition + cellPadding + headerFontSize * 0.35;
      for (const line of lines) {
        pdf.text(line, cellX, lineY);
        lineY += headerFontSize * 0.45;
      }
    }

    // Draw header borders
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.4);
    pdf.line(marginLeft, yPosition - 1, marginLeft + contentWidth, yPosition - 1); // top
    pdf.line(marginLeft, yPosition - 1 + headerH, marginLeft + contentWidth, yPosition - 1 + headerH); // bottom

    yPosition += headerH;

    // Data rows
    for (const row of rows) {
      const rowH = calcRowHeight(row, cellFontSize);
      ensureSpace(rowH + 2);

      pdf.setFont('times', 'normal');
      pdf.setFontSize(cellFontSize);
      pdf.setTextColor(0, 0, 0);

      for (let c = 0; c < numCols; c++) {
        const cellX = marginLeft + c * colWidth + cellPadding;
        const cellText = (row[c] || '').replace(/\*\*/g, '');
        const lines = pdf.splitTextToSize(cellText, colWidth - cellPadding * 2);
        let lineY = yPosition + cellPadding + cellFontSize * 0.35;
        for (const line of lines) {
          pdf.text(line, cellX, lineY);
          lineY += cellFontSize * 0.45;
        }
      }

      // Row bottom border
      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.2);
      pdf.line(marginLeft, yPosition + rowH, marginLeft + contentWidth, yPosition + rowH);

      yPosition += rowH;
    }

    // Vertical borders for entire table
    pdf.setDrawColor(180, 180, 180);
    pdf.setLineWidth(0.2);
    for (let c = 0; c <= numCols; c++) {
      const x = marginLeft + c * colWidth;
      // We need the table start Y... approximate
    }

    // Outer borders
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.3);
    // left and right vertical
    const tableStartY = yPosition - rows.reduce((sum, r) => sum + calcRowHeight(r, cellFontSize), 0) - headerH - 1;
    pdf.line(marginLeft, tableStartY, marginLeft, yPosition);
    pdf.line(marginLeft + contentWidth, tableStartY, marginLeft + contentWidth, yPosition);

    yPosition += 5;
  };

  // Helper: ensure heading + at least some content stays together (anti-orphan)
  const ensureHeadingSpace = (headingHeight: number) => {
    // Require at least heading + 20mm of content below it
    ensureSpace(headingHeight + 20);
  };

  // ====== START RENDERING ======
  addHeader();
  yPosition = marginTop + 5;

  const lines = content.split('\n');
  let i = 0;

  while (i < lines.length) {
    const trimmedLine = lines[i].trim();

    // Skip horizontal rules
    if (/^[-_*]{3,}$/.test(trimmedLine)) {
      i++;
      continue;
    }

    // Empty line
    if (!trimmedLine) {
      yPosition += 3;
      i++;
      continue;
    }

    // Tables
    if (trimmedLine.startsWith('|')) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i].trim());
        i++;
      }
      renderPdfTable(tableLines);
      continue;
    }

    // H1 - Título principal: 16pt bold, centrado
    if (trimmedLine.startsWith('# ') && !trimmedLine.startsWith('## ')) {
      const titleText = trimmedLine.substring(2).replace(/\*\*/g, '');
      ensureHeadingSpace(14);
      pdf.setFont('times', 'bold');
      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      const titleLines = pdf.splitTextToSize(titleText, contentWidth);
      for (const tl of titleLines) {
        ensureSpace(9);
        const textWidth = pdf.getTextWidth(tl);
        pdf.text(tl, marginLeft + (contentWidth - textWidth) / 2, yPosition);
        yPosition += 9;
      }
      yPosition += 6;
      // Draw a subtle line under the title
      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.3);
      pdf.line(marginLeft + 20, yPosition - 4, pageWidth - marginRight - 20, yPosition - 4);
      yPosition += 4;
      i++;
      continue;
    }

    // H2 - Sección: 14pt bold
    if (trimmedLine.startsWith('## ') && !trimmedLine.startsWith('### ')) {
      const sectionText = trimmedLine.substring(3).replace(/\*\*/g, '');
      ensureHeadingSpace(12);
      yPosition += 4;
      pdf.setFont('times', 'bold');
      pdf.setFontSize(14);
      pdf.setTextColor(0, 0, 0);
      const sectionLines = pdf.splitTextToSize(sectionText, contentWidth);
      for (const sl of sectionLines) {
        ensureSpace(8);
        pdf.text(sl, marginLeft, yPosition);
        yPosition += 8;
      }
      yPosition += 3;
      i++;
      continue;
    }

    // H3 - Subsección: 12pt bold
    if (trimmedLine.startsWith('### ')) {
      const subText = trimmedLine.substring(4).replace(/\*\*/g, '');
      ensureHeadingSpace(10);
      yPosition += 2;
      pdf.setFont('times', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      const subLines = pdf.splitTextToSize(subText, contentWidth);
      for (const sl of subLines) {
        ensureSpace(7);
        pdf.text(sl, marginLeft, yPosition);
        yPosition += 7;
      }
      yPosition += 2;
      i++;
      continue;
    }

    // H4+ - 11pt bold
    if (trimmedLine.startsWith('#### ')) {
      const h4Text = trimmedLine.substring(5).replace(/\*\*/g, '');
      ensureHeadingSpace(8);
      yPosition += 1;
      pdf.setFont('times', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(0, 0, 0);
      pdf.text(h4Text, marginLeft, yPosition);
      yPosition += 7;
      i++;
      continue;
    }

    // Nested bullet list (lines starting with - - or  -)
    if (trimmedLine.match(/^[-*]\s+[-*]\s+/) || trimmedLine.match(/^\s{2,}[-*]\s+/)) {
      const text = trimmedLine.replace(/^[-*]\s+[-*]\s+/, '').replace(/^\s{2,}[-*]\s+/, '');
      ensureSpace(7);
      printFormattedParagraph('– ' + text, 11, 'normal', 12, 2);
      i++;
      continue;
    }

    // Bullet list
    if (trimmedLine.match(/^[-*]\s+/)) {
      const text = trimmedLine.replace(/^[-*]\s+/, '');
      ensureSpace(7);
      printFormattedParagraph('• ' + text, 12, 'normal', 5, 2);
      i++;
      continue;
    }

    // Numbered list
    if (trimmedLine.match(/^\d+\.\s+/)) {
      ensureSpace(7);
      printFormattedParagraph(trimmedLine, 12, 'normal', 5, 2);
      i++;
      continue;
    }

    // Regular paragraph with inline bold support
    ensureSpace(7);
    printFormattedParagraph(trimmedLine, 12, 'normal', 0, 3);
    i++;
  }

  // Add footers to all pages
  const totalPages = pdf.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    pdf.setPage(p);
    addFooter(p, totalPages);
  }

  return pdf;
}

export async function exportToPDF(content: string, filename: string, chatMode?: ChatMode): Promise<void> {
  const pdf = await createPdfDocument(content, filename, chatMode);
  const safeFilename = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
  pdf.save(safeFilename);
}

export async function exportToPdfBlob(content: string, filename: string, chatMode?: ChatMode): Promise<Blob> {
  const pdf = await createPdfDocument(content, filename, chatMode);
  return pdf.output('blob');
}
